/**
 * The three reasons this script decides, in the visitor's browser, not to count.
 *
 * All three are decisions the server cannot make for us, and each one is a
 * question a customer will ask on day one.
 */

/**
 * Did the visitor ask not to be followed?
 *
 * Honouring this is odd for Panel and it is done anyway. Do Not Track is a
 * request not to be *tracked across sites*, and Panel cannot do that: the
 * identifier is a hash of a salt that is destroyed every night, so there is no
 * thread to follow from one day to the next, let alone from one site to
 * another. On the letter of it, the header does not apply to us.
 *
 * It is respected for two reasons that outrank the letter:
 *
 *  - a visitor who set it stated a preference, and "your preference does not
 *    technically apply to my implementation" is the argument every tracker
 *    makes. A product whose entire claim is about restraint cannot make it.
 *  - it costs a customer very little. DNT is off by default everywhere and
 *    Safari and Chrome removed the toggle; the population that still sends it
 *    is small and self-selected, and the alternative — a consent banner — costs
 *    far more traffic than this ever will.
 *
 * `globalPrivacyControl` is the successor signal, carried by law in several US
 * states, and is treated identically. `msDoNotTrack` and the window-level
 * property are the two legacy spellings that are still out there.
 *
 * NOTE — `navigator.webdriver` is deliberately NOT consulted here. Filtering
 * bots is real and necessary, and it belongs on the server, which sees the
 * user agent, the request rate and the IP the browser cannot show us. Doing it
 * here would mean a headless browser could turn the filter off by lying, and it
 * would make this script's own browser suite untestable — Playwright sets that
 * flag.
 */
export function optedOut(win: Window): boolean {
  try {
    const nav = win.navigator as Navigator & {
      msDoNotTrack?: string;
      globalPrivacyControl?: boolean;
    };
    if (nav.globalPrivacyControl === true) return true;
    const dnt = nav.doNotTrack || (win as Window & { doNotTrack?: string }).doNotTrack || nav.msDoNotTrack;
    return dnt === '1' || dnt === 'yes';
  } catch {
    // A `navigator` that throws is not a visitor who opted out.
    return false;
  }
}

/** Hostnames that are a development machine and never a site with visitors. */
const LOCAL_HOST = /^(localhost|127(?:\.\d{1,3}){3}|\[?::1\]?|0\.0\.0\.0)$/i;

/**
 * Suffixes reserved by the IETF for exactly this — they resolve to nothing on
 * the public internet, so a page served from one is somebody's laptop or
 * somebody's docs. `.local` is mDNS, which is the shape of a machine on a
 * home or office network.
 */
const LOCAL_SUFFIX = /\.(?:local|localhost|test|example|invalid)$/i;

/**
 * Is this page a developer looking at their own work?
 *
 * Excluded by default, and this is the one default in the script that costs a
 * customer data rather than saving them any. It is right anyway: the first
 * thing anybody does after pasting the tag is reload their dev server twenty
 * times, and a dashboard whose first day of data is the developer's own
 * refreshes teaches them the numbers are junk before they have seen a real one.
 *
 * **The way back in is an attribute, and it has to be**, which is worth saying
 * out loud because it looks like a worse design than the competition's. Other
 * tools let you set `localStorage.<tool>_ignore = false` from the console to
 * test an install locally. Panel writes nothing to a visitor's device — that is
 * the product — so there is no console switch to offer, and the override is
 * `data-panel-local="true"` on the tag itself. A constraint the brief imposes,
 * showing up in the shape of the API, rather than a hole in it.
 */
export function isLocal(loc: Location): boolean {
  try {
    const protocol = loc.protocol;
    if (protocol === 'file:' || protocol === 'about:' || protocol === 'data:') return true;
    const host = loc.hostname || '';
    return LOCAL_HOST.test(host) || LOCAL_SUFFIX.test(host);
  } catch {
    return false;
  }
}
